import {
  extractGraphQLErrors,
  getGraphqlErrorCode
} from 'src/entities/order/lib/graphql-error.utils';
import {
  formatUserMessageText,
  resolveUserMessage,
  UserMessage
} from 'src/entities/order/lib/order-user-messages';
import { EditAmountWarning } from '../model/edit-amount.model';

export const EDIT_AMOUNT_ERROR_MESSAGES: Record<string, UserMessage> = {
  SERVICE_NOT_FOUND: {
    title: 'Servicio no encontrado',
    body: 'No se encontró el servicio.'
  },
  TICKET_NOT_FOUND: {
    title: 'Ticket no encontrado',
    body: 'No se encontró el ticket.'
  },
  SERVICE_DELETED: {
    title: 'Servicio eliminado',
    body: 'Está eliminado y no se puede editar.'
  },
  TICKET_DELETED: {
    title: 'Ticket eliminado',
    body: 'Está eliminado y no se puede editar.'
  },
  AMOUNT_INVALID: {
    title: 'Monto inválido',
    body: 'El monto debe ser mayor a 0.'
  },
  EXCHANGE_RATE_MISSING: {
    title: 'Sin tipo de cambio',
    body: 'Falta tipo de cambio válido. No se puede convertir el monto.'
  },
  CURRENCY_INVALID: {
    title: 'Moneda inválida',
    body: 'Moneda inválida (se espera S o D).'
  },
  ORDER_ALREADY_EXISTS: {
    title: 'Documento existente',
    body: 'Ya tiene Orden de Servicio (OS) o Nota de Pedido (NP); no se puede editar el monto.'
  },
  OS_ALREADY_EXISTS: {
    title: 'Documento existente',
    body: 'Ya tiene Orden de Servicio (OS) o Nota de Pedido (NP); no se puede editar el monto.'
  },
  TICKET_HAS_ORDER: {
    title: 'Ticket con OS o NP',
    body: 'El ticket ya tiene OS o NP; no se puede editar.'
  },
  PURCHASE_NOTE_ALREADY_EXISTS: {
    title: 'Nota de Pedido existente',
    body: 'Ya tiene Nota de Pedido (NP); no se puede editar el monto.'
  },
  GROUPED_NOT_APPLICABLE: {
    title: 'Agrupado no aplica',
    body: 'La edición agrupada solo aplica a Personal de campo / Guía.'
  },
  GROUPED_SERVICES_NOT_FOUND: {
    title: 'Sin agrupados',
    body: 'No hay servicios agrupados editables.'
  },
  SERVICE_TICKET_MISMATCH: {
    title: 'Ticket inválido',
    body: 'El ticket no pertenece a ese servicio.'
  },
  ITINERARY_NOT_FOUND: {
    title: 'Itinerario no encontrado',
    body: 'No se pudo actualizar el costo del itinerario. Reintenta.'
  },
  ITINERARY_UPDATE_FAILED: {
    title: 'Error al actualizar itinerario',
    body: 'No se pudo actualizar el costo del itinerario. Reintenta.'
  }
};

const ORDER_BLOCK_CODES = new Set([
  'ORDER_ALREADY_EXISTS',
  'OS_ALREADY_EXISTS',
  'TICKET_HAS_ORDER',
  'PURCHASE_NOTE_ALREADY_EXISTS'
]);

export function isEditAmountBlockedByOrder(error: unknown): boolean {
  const code = getGraphqlErrorCode(error);
  return code != null && ORDER_BLOCK_CODES.has(code);
}

export function resolveEditAmountUserMessage(error: unknown): UserMessage {
  const code = getGraphqlErrorCode(error);
  const gqlMessage = extractGraphQLErrors(error)[0]?.message?.trim();

  const mapped = resolveUserMessage(
    code,
    [EDIT_AMOUNT_ERROR_MESSAGES],
    'Error al guardar el monto. Reintenta.'
  );

  // Preferir message ES del back si viene y no es genérico.
  if (gqlMessage && gqlMessage.length > 0 && !/^[A-Z_]+$/.test(gqlMessage)) {
    return { ...mapped, body: gqlMessage };
  }

  return mapped;
}

export function formatEditAmountErrorText(error: unknown): string {
  return formatUserMessageText(resolveEditAmountUserMessage(error));
}

export function getEditAmountWarningToast(
  warning: EditAmountWarning
): { icon: 'info' | 'warning'; title: string; text: string } | null {
  const code = String(warning.code ?? '').trim();
  const message =
    warning.message?.trim() ||
    'La operación se completó con una advertencia.';

  switch (code) {
    case 'IDEMPOTENT_NO_CHANGE':
      return {
        icon: 'info',
        title: 'Sin cambios',
        text: 'El monto ya estaba actualizado.'
      };
    case 'AUDIT_WRITE_FAILED':
      return {
        icon: 'warning',
        title: 'Auditoría',
        text: message
      };
    case 'GROUPED_SIBLING_SKIPPED_HAS_OS':
      return {
        icon: 'info',
        title: 'Agrupados parciales',
        text:
          message ||
          'Algunos servicios agrupados no se actualizaron porque ya tienen OS o NP.'
      };
    case 'GROUPED_SIBLING_SKIPPED_HAS_NP':
      return {
        icon: 'info',
        title: 'Agrupados parciales',
        text:
          message ||
          'Algunos servicios agrupados no se actualizaron porque ya tienen Nota de Pedido (NP).'
      };
    case 'TICKET_SIBLING_NOT_SYNCED':
      return {
        icon: 'info',
        title: 'Ticket hermano',
        text:
          message ||
          'El servicio TICKET no sincronizó un ticket hermano.'
      };
    default:
      if (!code && !warning.message) {
        return null;
      }
      return { icon: 'warning', title: 'Advertencia', text: message };
  }
}
